import React, { useState } from 'react';
import styled, { keyframes } from 'styled-components';

interface HelpRulesModalProps { 
  open: boolean; 
  onClose: () => void; 
}

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const slideUp = keyframes`
  from { 
    transform: translateY(24px); 
    opacity: 0; 
  }
  to { 
    transform: translateY(0); 
    opacity: 1; 
  }
`;

const pulse = keyframes`
  0% { transform: scale(1); }
  50% { transform: scale(1.05); }
  100% { transform: scale(1); }
`;

const slideInLeft = keyframes`
  from { 
    transform: translateX(-20px); 
    opacity: 0; 
  }
  to { 
    transform: translateX(0); 
    opacity: 1; 
  }
`;

const Overlay = styled.div<{ $open: boolean }>`
  position: fixed;
  inset: 0;
  z-index: 1500;
  background: rgba(0, 0, 0, .55);
  backdrop-filter: blur(10px) saturate(160%);
  display: ${p => p.$open ? 'flex' : 'none'};
  align-items: center;
  justify-content: center;
  animation: ${fadeIn} .28s ease;
`;

const Modal = styled.article`
  background: linear-gradient(135deg, rgba(255, 255, 255, .16), rgba(255, 255, 255, .08)), 
              var(--theme-card-background, #ffffff);
  color: var(--theme-text, #222);
  width: min(860px, 96vw);
  max-height: 88vh;
  border-radius: 30px;
  padding: 44px 46px 50px;
  box-shadow: 0 28px 78px -18px rgba(0, 0, 0, .6), 0 6px 20px rgba(0, 0, 0, .35);
  overflow-y: auto;
  animation: ${slideUp} .36s cubic-bezier(.22, 1.12, .32, 1);
  border: 1px solid rgba(255, 255, 255, .25);
  line-height: 1.5;
  font-size: 15px;
`;

const CloseBtn = styled.button`
  position: absolute;
  top: 12px;
  right: 14px;
  background: transparent;
  border: none;
  font-size: 34px;
  line-height: 1;
  cursor: pointer;
  color: var(--theme-text, #444);
  transition: transform .2s;
  border-radius: 50%;
  width: 48px;
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
  
  &:hover {
    transform: scale(1.1);
    background: rgba(255, 0, 0, .1);
    color: #ff3333;
  }
`;

const H1 = styled.h1`
  margin: 0 0 4px;
  font-size: 28px;
  display: flex;
  align-items: center;
  gap: 8px;
  animation: ${slideInLeft} .5s ease .2s both;
`;

const Section = styled.section<{ $delay?: number }>`
  margin: 26px 0 0;
  animation: ${slideInLeft} .4s ease ${p => (p.$delay || 0) * 0.1}s both;
  
  & > h2 {
    margin: 0 0 6px;
    font-size: 20px;
    color: var(--theme-primary, #2563eb);
    display: flex;
    align-items: center;
    gap: 8px;
  }
  
  & > h3 {
    margin: 18px 0 4px;
    font-size: 16px;
    color: var(--theme-secondary, #1d4ed8);
  }
`;

const Inline = styled.span`
  font-weight: 600;
  color: var(--theme-primary, #2563eb);
`;

const KW = styled.code`
  background: var(--theme-primary, #2563eb);
  color: #fff;
  padding: 3px 8px;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 600;
`;

const List = styled.ul`
  margin: 6px 0 14px 22px;
  padding: 0;
`;

const ListItem = styled.li`
  margin: 8px 0;
  transition: transform .2s, background-color .2s;
  padding: 6px 12px;
  border-radius: 8px;
  cursor: pointer;
  
  &:hover {
    transform: translateX(8px);
    background: rgba(37, 99, 235, .05);
  }
`;

const Tag = styled.span`
  display: inline-block;
  background: linear-gradient(135deg, var(--theme-primary, #2563eb), var(--theme-secondary, #1d4ed8));
  color: #fff;
  padding: 4px 10px;
  border-radius: 18px;
  font-size: 12px;
  font-weight: 600;
  letter-spacing: .5px;
  vertical-align: middle;
  animation: ${pulse} 2s infinite;
`;

const TabContainer = styled.div`
  display: flex;
  gap: 12px;
  margin: 20px 0;
  flex-wrap: wrap;
`;

const TabBtn = styled.button<{ $active: boolean }>`
  background: ${p => p.$active 
    ? 'linear-gradient(135deg, var(--theme-primary, #2563eb), var(--theme-secondary, #1d4ed8))' 
    : 'transparent'};
  color: ${p => p.$active ? '#fff' : 'var(--theme-text, #666)'};
  border: 2px solid var(--theme-primary, #2563eb);
  padding: 8px 16px;
  border-radius: 20px;
  font-weight: 600;
  cursor: pointer;
  font-size: 14px;
  transition: all .25s;
  transform: ${p => p.$active ? 'scale(1.05)' : 'scale(1)'};
  
  &:hover {
    background: ${p => p.$active 
      ? 'linear-gradient(135deg, var(--theme-primary, #2563eb), var(--theme-secondary, #1d4ed8))' 
      : 'rgba(37, 99, 235, .1)'};
    transform: scale(1.05);
  }
`;

const ScoreExample = styled.div`
  background: linear-gradient(135deg, rgba(37, 99, 235, .1), rgba(29, 78, 216, .05));
  border: 2px solid rgba(37, 99, 235, .2);
  border-radius: 16px;
  padding: 16px;
  margin: 12px 0;
  display: flex;
  align-items: center;
  gap: 12px;
`;

const DiceIcon = styled.span`
  font-size: 20px;
`;

const Footer = styled.div`
  margin-top: 34px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
`;

const Btn = styled.button`
  background: linear-gradient(135deg, var(--theme-primary, #2563eb), var(--theme-secondary, #1d4ed8));
  color: #fff;
  border: none;
  padding: 14px 26px;
  border-radius: 16px;
  font-weight: 600;
  cursor: pointer;
  font-size: 15px;
  box-shadow: 0 6px 18px -4px rgba(0, 0, 0, .5);
  transition: transform .2s, box-shadow .25s;
  
  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 10px 26px -8px rgba(0, 0, 0, .6);
  }
  
  &:active {
    transform: translateY(-1px) scale(.97);
  }
`;

const VersionInfo = styled.div`
  background: rgba(0, 0, 0, .05);
  border-radius: 12px;
  padding: 8px 16px;
  font-size: 13px;
  color: var(--theme-text, #666);
`;

const HelpRulesModal: React.FC<HelpRulesModalProps> = ({ open, onClose }) => {
  const [activeTab, setActiveTab] = useState<'classic' | 'maxi' | 'tips'>('classic');

  return (
    <Overlay $open={open} onClick={onClose} role="dialog" aria-modal="true" aria-label="Spelregler">
      <Modal onClick={e => e.stopPropagation()}>
        <CloseBtn aria-label="Stäng" onClick={onClose}>×</CloseBtn>
        <H1>🎲 Spelregler <Tag>Interaktiv guide</Tag></H1>
        <p>Komplett regelguide för alla Yatzy-varianter med interaktiva exempel och strategitips.</p>
        
        <TabContainer>
          <TabBtn $active={activeTab === 'classic'} onClick={() => setActiveTab('classic')}>
            🎯 Klassisk Yatzy
          </TabBtn>
          <TabBtn $active={activeTab === 'maxi'} onClick={() => setActiveTab('maxi')}>
            🎲 Maxi Yatzy
          </TabBtn>
          <TabBtn $active={activeTab === 'tips'} onClick={() => setActiveTab('tips')}>
            💡 Strategitips
          </TabBtn>
        </TabContainer>

        {activeTab === 'classic' && (
          <>
            <Section $delay={1}>
              <h2>🎯 Klassisk Yatzy (5 tärningar)</h2>
              <p>Kasta 5 tärningar upp till 3 gånger per omgång. Lås valfria tärningar mellan kast och välj sedan en kategori att fylla.</p>
              
              <h3>📊 Övre sektion</h3>
              <p>Samla samma siffror för att få poäng:</p>
              <ScoreExample>
                <DiceIcon>⚀⚀⚀</DiceIcon>
                <span><strong>Ettor:</strong> 3×1 = 3 poäng</span>
              </ScoreExample>
              <ScoreExample>
                <DiceIcon>⚅⚅⚅⚅</DiceIcon>
                <span><strong>Sexor:</strong> 4×6 = 24 poäng</span>
              </ScoreExample>
              <p>💰 <Inline>Bonus 50 poäng</Inline> om totalen blir ≥ 63 i övre sektionen!</p>
              
              <h3>🎨 Nedre sektion</h3>
              <List>
                <ListItem><Inline>Ett par:</Inline> Två lika tärningar → högsta par × 2</ListItem>
                <ListItem><Inline>Två par:</Inline> Två olika par → summa av båda paren</ListItem>
                <ListItem><Inline>Triss:</Inline> Tre lika → värde × 3</ListItem>
                <ListItem><Inline>Fyrtal:</Inline> Fyra lika → värde × 4</ListItem>
                <ListItem><Inline>Liten stege:</Inline> 1-2-3-4-5 → <strong>15 poäng</strong></ListItem>
                <ListItem><Inline>Stor stege:</Inline> 2-3-4-5-6 → <strong>20 poäng</strong></ListItem>
                <ListItem><Inline>Kåk:</Inline> Triss + par → summa av alla tärningar</ListItem>
                <ListItem><Inline>Chans:</Inline> Vad som helst → summa av alla tärningar</ListItem>
                <ListItem><Inline>YATZY:</Inline> Fem lika → <strong>50 poäng!</strong></ListItem>
              </List>
            </Section>
          </>
        )}

        {activeTab === 'maxi' && (
          <>
            <Section $delay={1}>
              <h2>🎲 Maxi Yatzy (6 tärningar)</h2>
              <p>Utökad version med 6 tärningar och fler spännande kategorier att fylla!</p>
              
              <h3>📊 Övre sektion</h3>
              <p>💰 <Inline>Bonus 50 poäng</Inline> om totalen blir ≥ 84 i övre sektionen!</p>
              
              <h3>🎨 Nedre sektion</h3>
              <List>
                <ListItem><Inline>Ett par / Två par:</Inline> Samma som klassisk men med 6 tärningar</ListItem>
                <ListItem><Inline>Triss / Fyrtal / Femtal:</Inline> 3/4/5 lika → värde × antal</ListItem>
                <ListItem><Inline>Liten stege:</Inline> 1-2-3-4-5 → 15 poäng</ListItem>
                <ListItem><Inline>Stor stege:</Inline> 2-3-4-5-6 → 20 poäng</ListItem>
                <ListItem><Inline>Full stege:</Inline> 1-2-3-4-5-6 → <strong>21 poäng</strong></ListItem>
                <ListItem><Inline>Hus:</Inline> Två olika triss → summa (3-3-3-5-5-5)</ListItem>
                <ListItem><Inline>Torn:</Inline> Fyrtal + par → summa (4-4-4-4-2-2)</ListItem>
                <ListItem><Inline>Chans:</Inline> Summa av alla 6 tärningar</ListItem>
                <ListItem><Inline>YATZY:</Inline> Sex lika → <strong>100 poäng!</strong></ListItem>
              </List>
              
              <ScoreExample>
                <DiceIcon>⚃⚃⚃⚃⚃⚃</DiceIcon>
                <span><strong>Maxi Yatzy:</strong> 6×4 = 100 poäng!</span>
              </ScoreExample>
            </Section>
          </>
        )}

        {activeTab === 'tips' && (
          <>
            <Section $delay={1}>
              <h2>💡 Pro-strategier</h2>
              
              <h3>🎯 Tidiga omgångar</h3>
              <List>
                <ListItem>Sikta på bonusen: behöver ≥3 i snitt per rad i övre sektionen</ListItem>
                <ListItem>Spara 4:or, 5:or och 6:or för höga poäng senare</ListItem>
                <ListItem>Undvik att "kasta bort" höga kombinationer tidigt</ListItem>
              </List>
              
              <h3>⚡ Mitt i spelet</h3>
              <List>
                <ListItem>Jaga stegar när du redan har 3-4 i sekvens</ListItem>
                <ListItem>Använd "Chans" för oväntat höga kast</ListItem>
                <ListItem>Var flexibel - anpassa strategin efter vad tärningarna ger</ListItem>
              </List>
              
              <h3>🎭 Slutspel</h3>
              <List>
                <ListItem>Fyll tomma kategorier strategiskt</ListItem>
                <ListItem>Kalkylera risk vs belöning för varje val</ListItem>
                <ListItem>Glöm inte att du kan sätta 0 i kategorier</ListItem>
              </List>
              
              <ScoreExample>
                <DiceIcon>💡</DiceIcon>
                <span><strong>Protip:</strong> Kom ihåg att du kan låsa och släppa tärningar flera gånger!</span>
              </ScoreExample>
            </Section>
          </>
        )}

        <Section $delay={2}>
          <h2>🎲 3D-spelbord & Spara-hylla</h2>
          <List>
            <ListItem><Inline>Spara-hylla:</Inline> Klicka på en tärning (eller tryck <KW>1</KW>–<KW>6</KW>) för att låta den glida ner i sitt upplysta fack i spara-hyllan</ListItem>
            <ListItem><Inline>Snabbval:</Inline> Använd knapparna under spelbordet för att spara par, triss, stege eller släppa alla med ett klick</ListItem>
            <ListItem><Inline>Byt 3D-bord:</Inline> Välj mellan Royal Casino, Nordisk Ek, Monte Carlo VIP, Cyber Obsidian och Kejserlig Marmor uppe till höger på bordet</ListItem>
          </List>
        </Section>

        <Section $delay={3}>
          <h2>🔧 Speciella regler</h2>
          <List>
            <ListItem><Inline>Hus (Maxi):</Inline> Kräver exakt två olika triss - inte fyrtal + par</ListItem>
            <ListItem><Inline>Torn (Maxi):</Inline> Kräver fyrtal + separat par med olika valörer</ListItem>
            <ListItem><Inline>Yatzy (Maxi):</Inline> Endast 6 lika ger poäng - 5 lika går till Femtal</ListItem>
          </List>
        </Section>

        <Footer>
          <VersionInfo>
            <KW>v1.8.4</KW> Senaste regeluppdatering
          </VersionInfo>
          <Btn onClick={onClose}>🎲 Börja spela!</Btn>
        </Footer>
      </Modal>
    </Overlay>
  );
};
export default HelpRulesModal;
